import React, { useState } from 'react';
import { Upload, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = `${BASE_URL}/api`

const PLACEHOLDER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="14">No Image</text></svg>`
)}`;

const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const cleaned = String(url).startsWith('/') ? String(url) : `/${url}`;
  return `${BASE_URL}${cleaned}`;
};

export const ImageUploadField = ({ label, value, onChange, onSave, token, placeholder = "Image URL" }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max size is 5MB.');
      event.target.value = '';
      return;
    }

    // Validate file type (match backend)
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and GIF images are allowed');
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      // Backend expects field name: file
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/upload/image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token || localStorage.getItem('token')}`,
            // Let the browser set the multipart boundary automatically.
          },
        }
      );

      const urlFromApi = response?.data?.url || response?.data?.path || response?.data;
      const imageUrl = toAbsoluteUrl(String(urlFromApi || ''));

      onChange?.(imageUrl);
      toast.success('Image uploaded!');

      // Auto-save immediately with the fresh value (important for settings)
      if (onSave) onSave(imageUrl);
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.message || error?.response?.data?.error;
      toast.error(msg ? `Failed to upload: ${msg}` : 'Failed to upload image');
    } finally {
      setUploading(false);
      // allow selecting the same file again
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {/* Upload Button */}
      <div className="flex gap-2">
        <div className="flex-1">
          <input
            type="file"
            id={`upload-${label.replace(/\s/g, '-')}`}
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label
            htmlFor={`upload-${label.replace(/\s/g, '-')}`}
            className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary transition-smooth bg-muted/30"
          >
            {uploading ? (
              <span className="text-sm">Uploading...</span>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">Upload from Computer</span>
              </>
            )}
          </label>
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setShowUrlInput(!showUrlInput)}
          title="Or paste URL"
          disabled={uploading}
        >
          <ImageIcon className="w-4 h-4" />
        </Button>
      </div>

      {/* URL Input (Collapsible) */}
      {showUrlInput && (
        <div className="flex gap-2">
          <Input
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
          />
          <Button type="button" onClick={() => onSave?.(value || '')} disabled={uploading}>
            Save URL
          </Button>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative inline-block">
          <img
            src={toAbsoluteUrl(value)}
            alt="Preview"
            className="w-32 h-32 object-cover rounded-lg border"
            onError={(e) => {
              e.currentTarget.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20width%3D%27128%27%20height%3D%27128%27%3E%0A%3Crect%20width%3D%27100%25%27%20height%3D%27100%25%27%20fill%3D%27%23eee%27/%3E%0A%3Ctext%20x%3D%2750%25%27%20y%3D%2750%25%27%20dominant-baseline%3D%27middle%27%20text-anchor%3D%27middle%27%20fill%3D%27%23777%27%20font-family%3D%27Arial%27%20font-size%3D%2712%27%3ENo%20Image%3C/text%3E%0A%3C/svg%3E';
            }}
          />
          <button
            type="button"
            onClick={() => {
              onChange?.('');
              if (onSave) onSave('');
            }}
            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground p-1 rounded-full hover:bg-destructive/90"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Upload image from your computer or paste image URL
      </p>
    </div>
  );
};
